import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X, History, Clock, Phone, ChevronRight, Loader2, Share2, Check } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_DOT = {
  ok: 'bg-emerald-400',
  matched: 'bg-emerald-400',
  fallback: 'bg-amber-400',
  skipped: 'bg-gray-500',
  error: 'bg-rose-400',
};

/* Right-side drawer that lists past executions (real inbound + live-test
   calls), and on click expands into a per-step audit view. Replaying a
   path also tells the canvas to re-highlight via `onReplay(exec)`. */
export default function ExecutionHistoryDrawer({ api, subAccountId, flowId, onClose, onReplay }) {
  const [executions, setExecutions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}/executions?limit=25`);
        setExecutions(Array.isArray(r.data) ? r.data : []);
      } catch (_e) {
        // empty list is fine
      } finally {
        setLoading(false);
      }
    })();
  }, [api, subAccountId, flowId]);

  const formatTime = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch { return '—'; }
  };

  return (
    <motion.aside
      initial={{ x: 480, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 480, opacity: 0 }}
      transition={{ type: 'spring', damping: 30, stiffness: 280 }}
      className="absolute top-0 right-0 bottom-0 w-[460px] bg-[#0c0d12] border-l border-white/10 flex flex-col z-50"
      data-testid="execution-history-drawer"
    >
      <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-sky-400/15 border border-sky-400/30 flex items-center justify-center">
          <History size={14} className="text-sky-300" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-white">Execution History</div>
          <div className="text-[10px] text-gray-400">Last {executions.length} call{executions.length === 1 ? '' : 's'}</div>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-white p-1" data-testid="history-close"><X size={16} /></button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 size={18} className="animate-spin text-gray-500" /></div>
        ) : executions.length === 0 ? (
          <div className="px-5 py-10 text-center text-xs text-gray-500">
            No executions yet — calls into this flow will show up here.
          </div>
        ) : selected ? (
          <ExecutionDetail
            exec={selected}
            onBack={() => setSelected(null)}
            onReplay={() => onReplay?.(selected)}
            formatTime={formatTime}
            api={api}
            subAccountId={subAccountId}
            flowId={flowId}
          />
        ) : (
          <ul className="divide-y divide-white/5">
            {executions.map((e) => (
              <li key={e.exec_id}>
                <button onClick={() => setSelected(e)} className="w-full px-5 py-3 text-left hover:bg-white/5 flex items-center gap-3" data-testid={`exec-row-${e.exec_id}`}>
                  <Phone size={14} className={`flex-shrink-0 ${e.was_diagnostic ? 'text-amber-300' : 'text-gray-400'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white truncate flex items-center gap-1.5">
                      {e.was_diagnostic && (
                        <span data-testid="exec-row-diagnostic-badge"
                          className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300 border border-amber-400/30">
                          Diagnostic
                        </span>
                      )}
                      <span className="truncate">{e.from_number || 'Unknown caller'}</span>
                    </div>
                    <div className="text-[10px] text-gray-400 flex items-center gap-1.5">
                      <Clock size={9} /> {formatTime(e.started_at)}
                      <span className="text-gray-600">·</span>
                      <span className={`uppercase font-bold tracking-wider ${
                        e.outcome === 'ai_agent' ? 'text-teal-300' :
                        e.outcome === 'route' ? 'text-sky-300' :
                        e.outcome === 'no_match' ? 'text-rose-300' : 'text-gray-400'
                      }`}>{e.outcome}</span>
                      {e.was_live_test && <span className="text-[9px] text-amber-300 font-bold ml-1">LIVE TEST</span>}
                    </div>
                  </div>
                  <ChevronRight size={14} className="text-gray-500" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </motion.aside>
  );
}

function ExecutionDetail({ exec, onBack, onReplay, formatTime, api, subAccountId, flowId }) {
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareExp, setShareExp] = useState('');

  // On mount, pull any live (un-revoked, un-expired) share token for
  // this exec so the URL is stable across re-opens of the drawer.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(
          `/api/sub-accounts/${subAccountId}/call-routing/${flowId}/executions/${exec.exec_id}/share`,
        );
        if (alive && r.data?.url) {
          setShareUrl(r.data.url);
          setShareExp(r.data.expires_at || '');
        }
      } catch (_e) { /* no existing token — fine */ }
    })();
    return () => { alive = false; };
  }, [api, subAccountId, flowId, exec.exec_id]);

  const handleShare = async () => {
    if (sharing) return;
    // If we already have a URL for this exec, just re-copy it — DON'T
    // re-mint a new token. (Backend now also de-dupes by exec_id but
    // this avoids the round-trip entirely.)
    if (shareUrl) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Share link copied');
      } catch (_e) {
        toast.error('Could not copy — select and copy manually from the box below');
      }
      return;
    }
    setSharing(true);
    try {
      const r = await api.post(
        `/api/sub-accounts/${subAccountId}/call-routing/${flowId}/executions/${exec.exec_id}/share`,
        { expires_in_hours: 168 },
      );
      const url = r.data?.url || '';
      setShareUrl(url);
      setShareExp(r.data?.expires_at || '');
      try {
        await navigator.clipboard.writeText(url);
        toast.success(r.data?.reused
          ? 'Existing share link copied'
          : 'Share link copied to clipboard — expires in 7 days');
      } catch (_e) {
        toast.success('Share link ready — copy it from the box below');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create share link');
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="p-5 space-y-4" data-testid="execution-detail">
      <button onClick={onBack} className="text-[11px] text-gray-400 hover:text-white">← Back to list</button>
      {exec.was_diagnostic && (
        <div
          data-testid="exec-detail-diagnostic-banner"
          className="rounded-xl border border-amber-400/30 bg-amber-400/10 p-3 text-[11px] leading-relaxed text-amber-100"
        >
          <div className="font-bold text-amber-200 mb-0.5 uppercase tracking-wider text-[10px]">
            Diagnostic run — not a real call
          </div>
          This trace was created by the <span className="font-semibold text-white">Verify &amp; Repair</span> tool
          using a spoofed caller (<span className="font-mono">+1 555 555 0100</span>) to test the flow
          end-to-end. No real customer was on the line, no Contact Timeline entry was created, and no
          credits were spent.
        </div>
      )}
      <div className="rounded-xl bg-white/5 border border-white/10 p-3 text-xs space-y-1">
        <div className="flex items-center justify-between"><span className="text-gray-400">From</span><span className="text-white font-mono">{exec.from_number || '—'}</span></div>
        <div className="flex items-center justify-between"><span className="text-gray-400">To</span><span className="text-white font-mono">{exec.to_number || '—'}</span></div>
        <div className="flex items-center justify-between"><span className="text-gray-400">Started</span><span className="text-white">{formatTime(exec.started_at)}</span></div>
        <div className="flex items-center justify-between"><span className="text-gray-400">Outcome</span><span className="text-teal-300 font-bold uppercase tracking-wider">{exec.outcome}</span></div>
        <div className="flex items-center justify-between"><span className="text-gray-400">Call SID</span><span className="text-white font-mono text-[10px] truncate ml-2 max-w-[200px]">{exec.call_sid || '—'}</span></div>
      </div>
      <button onClick={onReplay} className="w-full px-3 py-2 rounded-lg bg-teal-400/15 border border-teal-400/30 text-teal-300 text-xs font-bold hover:bg-teal-400/25" data-testid="exec-replay">
        Replay path on canvas
      </button>
      <button
        onClick={handleShare}
        disabled={sharing}
        className="w-full px-3 py-2 rounded-lg bg-sky-400/10 border border-sky-400/30 text-sky-300 text-xs font-bold hover:bg-sky-400/20 disabled:opacity-50 flex items-center justify-center gap-1.5"
        data-testid="exec-share"
      >
        {sharing ? <Loader2 size={12} className="animate-spin" /> : shareUrl ? <Check size={12} /> : <Share2 size={12} />}
        {shareUrl ? 'Copy link again' : 'Share replay link'}
      </button>
      {shareUrl && (
        <div className="rounded-lg bg-black/30 border border-white/10 p-2.5 space-y-1" data-testid="exec-share-link">
          <div className="text-[9px] uppercase tracking-wider text-gray-500">Public read-only link</div>
          <button
            onClick={() => {
              navigator.clipboard?.writeText(shareUrl).then(
                () => toast.success('Copied'),
                () => {},
              );
            }}
            className="block w-full text-left text-[10px] font-mono text-sky-200 hover:text-sky-100 break-all leading-tight"
            title="Click to copy"
          >
            {shareUrl}
          </button>
          {shareExp && (
            <div className="text-[9px] text-gray-500">Expires {new Date(shareExp).toLocaleString()}</div>
          )}
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Steps ({(exec.steps || []).length})</div>
        <ol className="space-y-2">
          {(exec.steps || []).map((s, i) => (
            <li key={i} className="rounded-lg border border-white/10 bg-white/5 p-3 text-xs">
              <div className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[s.status] || 'bg-gray-500'}`} />
                <span className="font-bold text-white uppercase tracking-wider text-[10px]">{s.type}</span>
                <span className="text-gray-500 text-[10px] ml-auto">{s.status}</span>
              </div>
              {s.reason && <div className="text-gray-300 mt-1">{s.reason}</div>}
              {s.data && Object.keys(s.data).length > 0 && (
                <pre className="mt-1.5 text-[10px] text-gray-400 bg-black/30 rounded px-2 py-1 overflow-x-auto">{JSON.stringify(s.data, null, 0)}</pre>
              )}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
